/**
 * Товари в резерві (звіт по reg.goods_reserved_movements).
 * 2026-08-22: канон Норми #29 — .card + InfiniteDocGrid (серверна
 * пагінація/сортування), фільтри рядком під назвою, підсумки з сервера.
 */
import { useMemo, useState } from 'react';
import { useOrgFilter, pickDefaultOrg } from '@/store/defaults';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { reports, type GoodsReservedRow } from '@/api/documents';
import { orgs } from '@/api/catalogs';
import DatePopover from '@/components/DatePopover';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';

function endOfToday(): string {
  const d = new Date();
  d.setHours(23, 59, 59, 0);
  const tz = -d.getTimezoneOffset();
  const sign = tz >= 0 ? '+' : '-';
  const pad = (n: number) => String(Math.floor(Math.abs(n))).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00${sign}${pad(tz / 60)}:${pad(tz % 60)}`;
}

const fmtQty = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: 4 });

export default function GoodsReservedPage() {
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // Орг за замовчуванням з шапки (override при ручному виборі).
  const [orgId, setOrgId] = useOrgFilter();
  const [onDate, setOnDate] = useState<string>(() => endOfToday());
  const [totalQty, setTotalQty] = useState<string | null>(null);

  if (!orgId && orgList.data?.items[0]) setOrgId(pickDefaultOrg(orgList.data.items)!.id);

  const { datasource, total } = useInfiniteGridDatasource<GoodsReservedRow>(
    async (limit, offset, sort) => {
      if (!orgId) return { items: [], total: 0 };
      const page = await reports.goodsReserved({
        organization_id: orgId,
        on_date: onDate,
        sort_by: sort?.sort_by,
        sort_dir: sort?.sort_dir,
        limit, offset,
      });
      setTotalQty(page.total_quantity);
      return { items: page.rows, total: page.total };
    },
    [orgId, onDate],
  );

  const cols = useMemo<ColDef<GoodsReservedRow>[]>(() => [
    { field: 'warehouse_code', headerName: 'Склад', width: 100 },
    { field: 'nomenclature_code', headerName: 'Код', width: 120 },
    { field: 'nomenclature_name', headerName: 'Номенклатура', flex: 1, minWidth: 220 },
    {
      field: 'quantity', headerName: 'Зарезервовано', width: 150,
      cellStyle: { textAlign: 'right' },
      cellRenderer: (p: { data?: GoodsReservedRow }) =>
        p.data ? <span style={{ fontWeight: 600 }}>{fmtQty(p.data.quantity)}</span> : '',
    },
    { field: 'customer_order_number', headerName: '№ замовлення', width: 150 },
    {
      field: 'customer_order_date', headerName: 'Дата замовлення', width: 150,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '',
    },
    { field: 'contractor_name', headerName: 'Покупець', flex: 1, minWidth: 180 },
  ], []);

  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>Товари в резерві<TitleTotal total={total} /></h2>
      </div>
      <div className="filters" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0 0 0.6rem', alignItems: 'center' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
          style={{ maxWidth: 260 }}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>на дату</span>
        <div style={{ width: 170, flex: '0 0 auto' }}>
          <DatePopover
            withTime
            value={onDate.slice(0, 16)}
            onChange={(v) => setOnDate(v + ':00+02:00')}
          />
        </div>
        {totalQty !== null && (
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginLeft: 'auto' }}>
            Усього зарезервовано (одиниць): <strong>{fmtQty(totalQty)}</strong>
          </span>
        )}
      </div>

      <InfiniteDocGrid<GoodsReservedRow>
        datasource={datasource}
        columnDefs={cols}
        storageKey="goods-reserved"
      />

      <p style={{ marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
        Список показує тільки активні резерви — кількість, що ще не відвантажена
        за відповідним замовленням покупця. При проведенні реалізації зі ссилкою
        на замовлення резерв автоматично знімається у відвантаженій кількості.
      </p>
    </div>
  );
}
